/* eslint-disable @angular-eslint/no-output-on-prefix */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {
  TableHeader,
  TableHeadersMeta,
} from 'src/app/core/interfaces/table.interface';

/** Row type with optional summary flag for styling */
export interface AccountStatementRow {
  isSummaryRow?: boolean;
  [key: string]: any;
}

/** Totals for current page, from rows with isSummaryRow === true only */
export interface PageSummaryTotals {
  totalDebit: number;
  totalCredit: number;
  totalBalance: number;
}

@Component({
  selector: 'app-table-account-statements',
  templateUrl: './table-account-statements.component.html',
  styleUrl: './table-account-statements.component.scss',
})
export class TableAccountStatementsComponent implements OnChanges {
  /** Available options: 20, 40, 80 rows per page */
  readonly pageSizes = [5, 10, 20, 50, 100];
  /** Current rows per page (default 20) */
  pageSize = 20;
  /** When false, all rows render and the paginator is hidden. */
  @Input() paginationEnabled = true;

  @Input({ required: true }) tableHeader: TableHeadersMeta;
  @Input({ required: false }) currentOrderBy?: string;
  @Input({ required: true }) selectStates: 'all' | 'some' | 'none';
  /** Full data array; pagination is applied internally */
  @Input({ required: true }) data: AccountStatementRow[] = [];
  /** Template for each row body (only <td> elements; <tr> is provided by this component). When rowTemplateProvidesTr is true, template must output one or more <tr> elements. */
  @Input({ required: true }) rowTemplate!: TemplateRef<any>;
  /** When true, rowTemplate is responsible for rendering <tr>(s); no wrapper <tr> is added. Use for responsive templates with expanded row. */
  @Input() rowTemplateProvidesTr = false;

  @Output() onSort = new EventEmitter<TableHeader>();
  @Output() onSelectAll = new EventEmitter<boolean>();
  /** Emits when current page or data changes; totals are from rows with isSummaryRow === true on current page only */
  @Output() pageSummaryTotalsChange = new EventEmitter<PageSummaryTotals>();

  @Input({ required: false }) maxVisibleColumns: number = Infinity;
  detailRowIndex: number | null = null;

  currentPage = 1;

  ngOnInit() {
    this.emitPageSummaryTotals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.currentPage = 1;
    }
    if (changes['data'] || changes['paginationEnabled']) {
      this.emitPageSummaryTotals();
    }
  }

  /** Totals for current page from rows where isSummaryRow === true only */
  getCurrentPageSummaryTotals(): PageSummaryTotals {
    const rows = (this.paginatedData || []).filter((r) => this.isSummaryRow(r));
    return {
      totalDebit: rows.reduce((s, r) => s + (Number((r as any).debitAmountLocal) || 0), 0),
      totalCredit: rows.reduce((s, r) => s + (Number((r as any).creditAmountLocal) || 0), 0),
      totalBalance: rows.reduce((s, r) => s + (Number((r as any).balanceLocal) || 0), 0),
    };
  }

  private emitPageSummaryTotals(): void {
    this.pageSummaryTotalsChange.emit(this.getCurrentPageSummaryTotals());
  }

  get totalRecords(): number {
    return this.data?.length ?? 0;
  }

  get totalPages(): number {
    if (!this.paginationEnabled) return 1;
    if (!this.totalRecords) return 1;
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  /** Rows for the current page only (max 20) */
  get paginatedData(): AccountStatementRow[] {
    if (!this.data?.length) return [];
    if (!this.paginationEnabled) return this.data;
    const start = (this.currentPage - 1) * this.pageSize;
    return this.data.slice(start, start + this.pageSize);
  }

  get startRecord(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    const end = this.currentPage * this.pageSize;
    return end > this.totalRecords ? this.totalRecords : end;
  }

  onPageChange(event: { currentPage: number; pageSize: number }): void {
    if (!this.paginationEnabled) return;
    if (this.pageSizes.includes(event.pageSize)) {
      this.pageSize = event.pageSize;
    }
    const maxPage = Math.max(1, this.totalPages);
    this.currentPage = Math.min(Math.max(1, event.currentPage), maxPage);
    this.emitPageSummaryTotals();
  }

  toggleSelectAll() {
    const isSelectAll = this.selectStates === 'all' ? false : true;
    this.onSelectAll.emit(isSelectAll);
  }

  getKey(header: TableHeader) {
    if (typeof header.key === 'function') {
      return header.key();
    }
    return header.key;
  }

  get visibleHeaders(): TableHeader[] {
    if (
      !this.tableHeader.isResposive ||
      this.tableHeader.headers.length <= this.maxVisibleColumns
    ) {
      return this.tableHeader.headers;
    }
    return this.tableHeader.headers.slice(0, this.maxVisibleColumns);
  }

  get hiddenHeaders(): TableHeader[] {
    if (
      !this.tableHeader.isResposive ||
      this.tableHeader.headers.length <= this.maxVisibleColumns
    ) {
      return [];
    }
    return this.tableHeader.headers.slice(this.maxVisibleColumns);
  }

  toggleDetailRow(rowIndex: number) {
    if (this.detailRowIndex === rowIndex) {
      this.detailRowIndex = null;
    } else {
      this.detailRowIndex = rowIndex;
    }
  }

  closeDetailRow() {
    this.detailRowIndex = null;
  }

  /** Index in the full data set for the row at position i on the current page */
  getRowIndex(pageRowIndex: number): number {
    if (!this.paginationEnabled) return pageRowIndex;
    return (this.currentPage - 1) * this.pageSize + pageRowIndex;
  }

  /** Detect if row should be styled as summary (isSummaryRow: true from API) */
  isSummaryRow(row: AccountStatementRow | null | undefined): boolean {
    return row != null && (row as any).isSummaryRow === true;
  }
}
